import { useMutation, useQuery } from '@apollo/client/react';
import { Button, Descriptions, Form, Input, message, Modal, Select, Spin, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Spacer } from '../../components/Spacer';
import { ADMIN_EXECUTOR, ADMIN_UPDATE_EXECUTOR_STATUS } from '../../gqls/executor';
import { executorCategories } from '../../utils';

export const PageExecutor = () => {
  const params = useParams();

  const [messageApi, contextHolder] = message.useMessage();

  const [changeStatusVisible, setChangeStatusVisible] = useState(false);
  const [changeStatus, setChangeStatus] = useState(false);
  const [changeMessage, setChangeMessage] = useState('');

  const { data, loading } = useQuery(ADMIN_EXECUTOR, { variables: { where: { id: params.id } } });

  const [adminUpdateExecutorStatus, { loading: adminUpdateExecutorStatusLoading }] = useMutation(
    ADMIN_UPDATE_EXECUTOR_STATUS,
    {
      onCompleted: () => {
        messageApi.success('Статус изменен');
        setChangeStatusVisible(false);
      },
      onError: () => {
        alert('Произошла ошибка, попробуйте еще раз');
      },
    },
  );

  const executor = data?.adminExecutor;
  const categories = executor?.categories?.length ? executor.categories : executor?.category ? [executor.category] : [];

  useEffect(() => {
    if (executor?.status) setChangeStatus(executor.status);
    if (executor?.statusMessage) setChangeMessage(executor.statusMessage);
  }, [executor?.status, executor?.statusMessage]);

  const categoryLabel = categories
    .map((item) => {
      const category = executorCategories.find((c) => c.value === item);
      if (item === 'heavy-truck' && executor?.heavyTruckType) {
        let heavyTruckType = '';
        if (executor.heavyTruckType === 'semitrailer') heavyTruckType = 'Полуприцеп';
        if (executor.heavyTruckType === 'trawl') heavyTruckType = 'Трал';
        return `${category?.label || item}${heavyTruckType ? ` (${heavyTruckType})` : ''}`;
      }
      return category?.label || item;
    })
    .join(', ');

  let status = undefined;
  if (executor?.status === 'new') status = <Tag color="blue">Новый</Tag>;
  if (executor?.status === 'approved') status = <Tag color="green">Одобрен</Tag>;
  if (executor?.status === 'cancelled') status = <Tag color="red">Отменен</Tag>;

  return (
    <>
      {contextHolder}
      <Typography.Title level={5} style={{ margin: 0 }}>
        Исполнитель
      </Typography.Title>
      <Spacer h={12} />
      {loading ? (
        <Spin />
      ) : executor ? (
        <>
          <Descriptions
            layout="vertical"
            bordered
            items={[
              {
                key: 'id',
                label: 'ID',
                children: executor.id,
              },
              {
                key: 'userId',
                label: 'Пользователь',
                children: <Link to={`/users/${executor.user.id}`}>{executor.user.id}</Link>,
              },
              {
                key: 'fullName',
                label: 'Имя',
                children: executor.fullName,
              },
              {
                key: 'category',
                label: 'Категория',
                children: categoryLabel || executor.category || '—',
              },
              {
                key: 'techniqueName',
                label: 'Название техники',
                children: executor.techniqueName || '—',
              },
              {
                key: 'number',
                label: 'Номер техники',
                children: executor.number || '—',
              },
              {
                key: 'status',
                label: 'Статус',
                children: status,
              },
              {
                key: 'statusMessage',
                label: 'Сообщение от модератора',
                children: <b>{executor.statusMessage || ''}</b>,
              },
              {
                key: 'workStatus',
                label: 'Статус работы',
                children: executor.workStatus === 'free' ? 'Свободен' : 'Занят',
              },
              {
                key: 'completedOrders',
                label: 'Кол-во выполненных заказов',
                children: executor.completedOrders,
              },
              {
                key: 'rating',
                label: 'Рейтинг',
                children: parseFloat(executor.rating).toFixed(1),
                span: 'filled',
              },
              {
                key: 'driverLicensePhotos',
                label: 'Фото прав',
                children: (
                  <>
                    {executor.driverLicensePhotos?.map((photo) => (
                      <a key={photo} href={photo} target="_blank" rel="noreferrer">
                        <img src={photo} alt="driverLicense" style={{ maxWidth: 120, marginRight: 8 }} />
                      </a>
                    ))}
                  </>
                ),
                span: 'filled',
              },
              {
                key: 'sorPhotos',
                label: 'Фото СОР',
                children: (
                  <>
                    {executor.sorPhotos?.map((photo) => (
                      <a key={photo} href={photo} target="_blank" rel="noreferrer">
                        <img src={photo} alt="driverLicense" style={{ maxWidth: 120, marginRight: 8 }} />
                      </a>
                    ))}
                  </>
                ),
                span: 'filled',
              },
              {
                key: 'techniquePhoto',
                label: 'Фото техники',
                children: (
                  <a href={executor.techniquePhoto} target="_blank" rel="noreferrer">
                    <img src={executor.techniquePhoto} alt="driverLicense" style={{ maxWidth: 120, marginRight: 8 }} />
                  </a>
                ),
                span: 'filled',
              },
            ]}
          />
          <Spacer h={20} />
          <Button onClick={() => setChangeStatusVisible(true)} type="primary">
            Изменить статус
          </Button>
          <Modal
            open={changeStatusVisible}
            onCancel={() => setChangeStatusVisible(false)}
            title="Изменить статус"
            footer={
              <>
                <Button type="default" onClick={() => setChangeStatusVisible(false)}>
                  Отмена
                </Button>
                <Button
                  type="primary"
                  loading={adminUpdateExecutorStatusLoading}
                  onClick={() => {
                    if (!changeStatus) return;
                    adminUpdateExecutorStatus({
                      variables: {
                        where: { id: executor.id },
                        data: { status: changeStatus, statusMessage: changeMessage },
                      },
                    });
                  }}
                >
                  Изменить
                </Button>
              </>
            }
          >
            <Form.Item label="Статус" required layout="vertical">
              <Select
                onChange={(v) => setChangeStatus(v)}
                value={changeStatus}
                placeholder="Статус"
                options={[
                  { label: 'Новый', value: 'new' },
                  { label: 'Одобрен', value: 'approved' },
                  { label: 'Отменен', value: 'cancelled' },
                ]}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Сообщение от модератора" layout="vertical">
              <Input.TextArea
                value={changeMessage}
                onChange={(e) => setChangeMessage(e.target.value)}
                style={{ width: '100%' }}
                rows={4}
              />
            </Form.Item>
          </Modal>
        </>
      ) : null}
    </>
  );
};
